<template>
  <div class="spinner-container" :class="{ on: isLoading }">
    <div class="spinner-bg">
      <div class="spinner" />
    </div>
  </div>
</template>

<script setup lang="ts">
const TLoading = {
  LOADING: 'LOADING',
  IDLE: 'IDLE'
}
// eslint-disable-next-line no-redeclare
type TLoading = (typeof TLoading)[keyof typeof TLoading]

const { more } = defineProps<{ more?:() => Promise<void> }>()

const THRED_HOLD = 0.7
const state = reactive({ loading: TLoading.IDLE, ended: false })
const isLoading = computed(() => state.loading === TLoading.LOADING)

onMounted(() => window.addEventListener('scroll', handleScroll))
onUnmounted(() => window.removeEventListener('scroll', handleScroll))
onActivated(() => window.addEventListener('scroll', handleScroll))
onDeactivated(() => window.removeEventListener('scroll', handleScroll))

const init = () => {
  state.loading = TLoading.IDLE
  state.ended = false
}

const loading = () => {
  state.loading = TLoading.LOADING
}

const loaded = () => {
  state.loading = TLoading.IDLE
}

const setEnded = (isEnded: boolean) => {
  state.ended = isEnded
}

const handleScroll = () => {
  if (isLoading.value || state.ended) {
    return
  }

  const { scrollTop, scrollHeight, clientHeight } = document.documentElement
  if (scrollTop + clientHeight >= scrollHeight * THRED_HOLD && !isLoading.value) {
    trigger()
  }
}

const trigger = async () => {
  if (more) {
    loading()
    await more()
    loaded()
  }
}

defineExpose({
  init,
  setEnded
})

</script>

<style scoped>
.spinner-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  z-index: -1000;
  transition: opacity 0.5s ease-in-out;
}
.spinner-container.on {
  position: fixed;
  z-index: 1000;
  opacity: 1;
}
.spinner-bg {
  margin: auto;
  background-color: rgba(255, 255, 255, 0.8);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.spinner {
  width: 32px;
  height: 32px;
  color: #9cdd7a;
  background: linear-gradient(currentColor 0 0),
  linear-gradient(currentColor 0 0),
  linear-gradient(currentColor 0 0),
  linear-gradient(currentColor 0 0);
  background-size: 17px 17px;
  background-repeat: no-repeat;
  animation: shapes-53h9rpsm 1.5s infinite cubic-bezier(0.3,1,0,1);
}

@keyframes shapes-53h9rpsm {
  0% {
    background-position: 0 0, 100% 0, 100% 100%, 0 100%;
  }

  33% {
    background-position: 0 0, 100% 0, 100% 100%, 0 100%;
    width: 48px;
    height: 48px;
  }

  66% {
    background-position: 100% 0, 100% 100%, 0 100%, 0 0;
    width: 48px;
    height: 48px;
  }

  100% {
    background-position: 100% 0, 100% 100%, 0 100%, 0 0;
  }
}
</style>
